/**
 * BlockHost — thin wrapper composing useBlockHost + RenderContext.Provider +
 * BlockV2 into a single drop-in component for shell callers.
 *
 * Usage:
 *   <BlockHost block={bottomBarBlock} overrides={…} storeActions={…} />
 *
 * When finer-grained control is needed (e.g. to forward the stateManager to
 * sibling UI or to intercept ctx), use `useBlockHost` directly.
 */

import { BlockV2 } from "@cogo/react-renderer-v2"
import { RenderContext } from "@cogo/react-renderer"
import {
  useCallback,
  useRef,
  type FormEvent,
  type MouseEvent as ReactMouseEvent,
} from "react"
import { collectRendererProjection } from "@/features/preview/services/actionflow-trace"
import type { UseBlockHostOptions } from "./use-block-host"
import { useBlockHost } from "./use-block-host"

const NATIVE_NAVIGATION_SELECTOR = [
  "a",
  "form",
  "[href]",
  "[target]",
  "[formaction]",
  "[formtarget]",
  '[data-action="navigate"]',
].join(",")

function containNativeNavigation(event: ReactMouseEvent<HTMLDivElement>): void {
  const target = event.target
  if (!(target instanceof Element) || !target.closest(NATIVE_NAVIGATION_SELECTOR)) {
    return
  }
  event.preventDefault()
  event.stopPropagation()
}

function containNativeFormSubmission(event: FormEvent<HTMLDivElement>): void {
  event.preventDefault()
  event.stopPropagation()
}

export function BlockHost({ block, overrides, storeActions, islands, hostServices, blockId, captureActionFlowTrace, interactionMode }: UseBlockHostOptions) {
  const hostRef = useRef<HTMLDivElement>(null)
  const getRendererProjection = useCallback(() => collectRendererProjection(hostRef.current), [])
  const { ctx, block: renderBlock } = useBlockHost({
    block,
    overrides,
    storeActions,
    islands,
    hostServices,
    blockId,
    captureActionFlowTrace,
    interactionMode,
    getRendererProjection,
  })
  const containsNativeNavigation = interactionMode === "agent-surface-preview"
  return (
    <div
      ref={hostRef}
      style={{ display: "contents" }}
      data-agent-surface-navigation-boundary={
        containsNativeNavigation ? "true" : undefined
      }
      onClickCapture={
        containsNativeNavigation ? containNativeNavigation : undefined
      }
      onAuxClickCapture={
        containsNativeNavigation ? containNativeNavigation : undefined
      }
      onSubmitCapture={
        containsNativeNavigation ? containNativeFormSubmission : undefined
      }
    >
      <RenderContext.Provider value={ctx}>
        <BlockV2 block={renderBlock} />
      </RenderContext.Provider>
    </div>
  )
}
